/**
 * TemplateUsageTab — how much the template is used and how well it performs. Demo page: /billing-and-plan (usage
 * cards) and /tables/basic-tables (compact lists inside cards).
 */
import React from 'react';
import { Badge, Card } from '../../../components/ui/index.js';
import type { AgentTemplate } from '../../../generated/api/types.gen.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { FactList, FactRow, formatDate, formatPercent } from './detail-parts.js';

export const TemplateUsageTab: React.FC<{ template: AgentTemplate }> = ({ template }) => {
  const evaluations = template.evaluationHistory?.promptEvaluations ?? [];
  const quality = template.evaluationHistory?.qualitySnapshots ?? [];
  const runs = template.evaluationHistory?.evaluationRuns ?? [];
  const usage = template.usageImpact;
  return (
    <div className="grid items-start gap-5 xl:grid-cols-3">
      <Card title="Usage">
        <FactList>
          <FactRow label="Workflow steps" value={usage?.workflowStepCount ?? 0} />
          <FactRow label="Active workflows" value={usage?.activeWorkflowCount ?? 0} />
          <FactRow label="Active daemons" value={usage?.activeDaemonCount ?? 0} />
          <FactRow label="Times used" value={template.usageCount ?? 0} />
          <FactRow label="Last used" value={formatDate(template.lastUsedAt)} />
        </FactList>
      </Card>
      <Card title="Evaluations">
        {evaluations.length === 0 ? (
          <p className="text-sm text-muted">No prompt evaluations are recorded.</p>
        ) : (
          <ul className="space-y-3">
            {evaluations.slice(0, 5).map((item) => (
              <li key={item.id} className="border-b pb-3 text-sm last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-heading">{sentenceCase(item.agentStatus ?? 'unknown')}</span>
                  <span className="text-muted">{formatPercent(item.promptScore)}</span>
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
                  <Badge
                    variant={item.contractPassed === false ? 'danger' : item.contractPassed ? 'success' : 'neutral'}
                  >
                    {item.contractPassed === null
                      ? 'Contract not checked'
                      : item.contractPassed
                        ? 'Contract passed'
                        : 'Contract failed'}
                  </Badge>
                  {formatDate(item.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Quality trend">
        {quality.length === 0 && runs.length === 0 ? (
          <p className="text-sm text-muted">No quality snapshots or evaluation runs are recorded.</p>
        ) : (
          <FactList>
            {quality.slice(0, 4).map((item) => (
              <FactRow
                key={`q-${item.id}`}
                label={formatDate(item.periodEnd)}
                value={`${formatPercent(item.qualityScore)} quality`}
              />
            ))}
            {runs.slice(0, 3).map((item) => (
              <FactRow
                key={`r-${item.id}`}
                label={item.datasetName}
                value={`${sentenceCase(item.status ?? 'unknown')} · ${formatPercent(item.avgScore)}`}
              />
            ))}
          </FactList>
        )}
      </Card>
    </div>
  );
};
